import WebVisitsReportViewEnum from '@/Enums/Attribution/WebVisits/ReportView.enum';
import MapScale from '@/Enums/Map/MapScale.enum';
import { RootState } from '@/store';
import { createSelector } from '@reduxjs/toolkit';
import { selectSharedApiLoading } from '../sharedApi.selectors';

export const selectWebVisits = (state: RootState) => state.webVisits;

export const selectWebVisitsFilterSource = createSelector(
  [selectWebVisits],
  ({ campaigns, placementOptions, eventOptions, campaignNameDataRequestId, pixelDataRequestId }) => ({
    campaigns,
    placementOptions,
    eventOptions,
    filterSourceLoading: !!(campaignNameDataRequestId || pixelDataRequestId),
  }),
);

export const selectWebVisitsFilters = createSelector([selectWebVisits], ({ filters }) => filters);

export const selectWebVisitsDateData = createSelector([selectWebVisits], ({ dateData, totals, dateDataRequestId }) => ({
  dateData,
  totals,
  dateDataLoading: !!dateDataRequestId,
}));

export const selectWebVisitsDeviceData = createSelector([selectWebVisits], ({ deviceData, deviceDataRequestId }) => ({
  deviceData,
  deviceDataLoading: !!deviceDataRequestId,
}));

export const selectWebVisitsChannelData = createSelector(
  [selectWebVisits],
  ({ channelData, channelDataRequestId }) => ({ channelData, channelDataLoading: !!channelDataRequestId }),
);

export const selectWebVisitsCampaignNameData = createSelector(
  [selectWebVisits],
  ({ campaignNameData, campaignNameDataRequestId }) => ({
    campaignNameData,
    campaignNameDataLoading: !!campaignNameDataRequestId,
  }),
);

export const selectWebVisitsCreativeData = createSelector(
  [selectWebVisits],
  ({ creativeData, creativeDataRequestId }) => ({
    creativeData,
    creativeDataLoading: !!creativeDataRequestId,
  }),
);

export const selectWebVisitsPublisherNameData = createSelector(
  [selectWebVisits],
  ({ publisherNameData, publisherNameDataRequestId }) => ({
    publisherNameData,
    publisherNameDataLoading: !!publisherNameDataRequestId,
  }),
);

export const selectWebVisitsPlacementData = createSelector(
  [selectWebVisits],
  ({ placementData, placementDataRequestId }) => ({
    placementData,
    placementDataLoading: !!placementDataRequestId,
  }),
);

export const selectWebVisitsDayOfWeekData = createSelector(
  [selectWebVisits],
  ({ dayOfWeekData, dayOfWeekDataRequestId }) => ({
    dayOfWeekData,
    dayOfWeekDataLoading: !!dayOfWeekDataRequestId,
  }),
);

export const selectWebVisitsDaytimeData = createSelector(
  [selectWebVisits],
  ({ dayTimeData, dayTimeDataRequestId }) => ({
    dayTimeData,
    dayTimeDataLoading: !!dayTimeDataRequestId,
  }),
);

export const selectWebVisitsPixelData = createSelector([selectWebVisits], ({ pixelData, pixelDataRequestId }) => ({
  pixelData,
  pixelDataLoading: !!pixelDataRequestId,
}));

export const selectWebVisitsStateData = createSelector([selectWebVisits], ({ stateData, stateDataRequestId }) => ({
  stateData,
  stateDataLoading: !!stateDataRequestId,
}));

export const selectWebVisitsDmaData = createSelector([selectWebVisits], ({ dmaData, dmaDataRequestId }) => ({
  dmaData,
  dmaDataLoading: !!dmaDataRequestId,
}));

export const selectWebVisitsCountyData = createSelector([selectWebVisits], ({ countyData, countyDataRequestId }) => ({
  countyData,
  countyDataLoading: !!countyDataRequestId,
}));

export const selectWebVisitsZipData = createSelector([selectWebVisits], ({ zipData, zipDataRequestId }) => ({
  zipData,
  zipDataLoading: !!zipDataRequestId,
}));

export const selectWebVisitCongressionalDistrictsData = createSelector(
  [selectWebVisits],
  ({ congressionalDistrictsData, congressionalDistrictsDataRequestId }) => ({
    congressionalDistrictsData,
    congressionalDistrictsDataLoading: !!congressionalDistrictsDataRequestId,
  }),
);

export const selectWebVisitsCityData = createSelector([selectWebVisits], ({ cityData, cityDataRequestId }) => ({
  cityData,
  cityDataLoading: !!cityDataRequestId,
}));

export const selectWebVisitsTableReports = createSelector(
  [
    selectWebVisitsDateData,
    selectWebVisitsChannelData,
    selectWebVisitsDeviceData,
    selectWebVisitsCampaignNameData,
    selectWebVisitsPlacementData,
    selectWebVisitsCreativeData,
    selectWebVisitsPublisherNameData,
    selectWebVisitsDayOfWeekData,
    selectWebVisitsDaytimeData,
    selectWebVisitsPixelData,
    selectWebVisitsStateData,
    selectWebVisitsZipData,
    selectWebVisitsCountyData,
    selectWebVisitCongressionalDistrictsData,
    selectWebVisitsCityData,
    selectWebVisitsDmaData,
  ],
  (
    { dateData, dateDataLoading },
    { channelData, channelDataLoading },
    { deviceData, deviceDataLoading },
    { campaignNameData, campaignNameDataLoading },
    { placementData, placementDataLoading },
    { creativeData, creativeDataLoading },
    { publisherNameData, publisherNameDataLoading },
    { dayOfWeekData, dayOfWeekDataLoading },
    { dayTimeData, dayTimeDataLoading },
    { pixelData, pixelDataLoading },
    { stateData, stateDataLoading },
    { zipData, zipDataLoading },
    { countyData, countyDataLoading },
    { congressionalDistrictsData, congressionalDistrictsDataLoading },
    { cityData, cityDataLoading },
    { dmaData, dmaDataLoading },
  ) => ({
    loading: {
      [WebVisitsReportViewEnum.Date]: dateDataLoading,
      [WebVisitsReportViewEnum.Channel]: channelDataLoading,
      [WebVisitsReportViewEnum.Device]: deviceDataLoading,
      [WebVisitsReportViewEnum.Campaign]: campaignNameDataLoading,
      [WebVisitsReportViewEnum.Placement]: placementDataLoading,
      [WebVisitsReportViewEnum.Creative]: creativeDataLoading,
      [WebVisitsReportViewEnum.Publisher]: publisherNameDataLoading,
      [WebVisitsReportViewEnum.DaysOfWeek]: dayOfWeekDataLoading,
      [WebVisitsReportViewEnum.Daypart]: dayTimeDataLoading,
      [WebVisitsReportViewEnum.Pixel]: pixelDataLoading,
      [MapScale.State]: stateDataLoading,
      [MapScale.Zip]: zipDataLoading,
      [MapScale.County]: countyDataLoading,
      [MapScale.CongressionalDistricts]: congressionalDistrictsDataLoading,
      [MapScale.City]: cityDataLoading,
      [MapScale.Dma]: dmaDataLoading,
    },
    data: {
      dateData,
      channelData,
      deviceData,
      campaignNameData,
      placementData,
      creativeData,
      publisherNameData,
      dayOfWeekData,
      dayTimeData,
      pixelData,
      stateData,
      zipData,
      countyData,
      congressionalDistrictsData,
      cityData,
      dmaData,
    },
  }),
);

export const selectWebVisitsFilterSourceLoading = createSelector(
  [selectWebVisits, selectWebVisitsCampaignNameData, selectWebVisitsPlacementData, selectSharedApiLoading],
  ({ campaigns: selectedCampaigns }, { campaignNameDataLoading }, { placementDataLoading }, sharedApiLoading) =>
    campaignNameDataLoading || sharedApiLoading || selectedCampaigns.length ? placementDataLoading : false,
);
